//
// Copyright (c) 2011-2018 by Curalate, Inc.
//

import React from 'react';
import styled, { css } from 'styled-components';
import { Button, Icon } from 'unison';

import { StreamSourceLabel } from '@streams/components/StreamSourceLabel';
import {
  StreamSourcePillType,
  StreamSourceTypes,
} from '@streams/constants/streamCreationConstants';
import { IStreamSource } from '@streams-drawer/model/IStreamSource';

interface Props {
  addedSources: IStreamSource[];
  hasOutstandingSaveRequest: boolean;
  removeSource: (source: IStreamSource) => void;
  defaultPillType: StreamSourcePillType;
}

interface ISourcePillProps {
  isDisabled?: boolean;
  pillType: StreamSourcePillType;
}

function computeDisplayLabel(source: IStreamSource) {
  const label = source.sourceLabel;

  switch (source.sourceType) {
    case StreamSourceTypes.USER:
    case StreamSourceTypes.MENTIONS_AND_TAGS: {
      return label.startsWith('@') ? label : `@${label}`;
    }
    case StreamSourceTypes.HASHTAG: {
      return label.startsWith('#') ? label : `#${label}`;
    }
    default:
      return label;
  }
}

export const StreamSourcesList = ({
  addedSources,
  defaultPillType,
  hasOutstandingSaveRequest,
  removeSource,
}: Props) => {
  const list = addedSources.map((addedSource, index) => {
    return (
      <StreamSourcesListItem
        key={index}
        isDisabled={hasOutstandingSaveRequest}
        pillType={defaultPillType}
      >
        <div className='cu-text-truncate' title={addedSource.sourceLabel}>
          <StreamSourceLabel labelText={computeDisplayLabel(addedSource)} />
        </div>
        <Button
          isDisabled={hasOutstandingSaveRequest}
          onClick={() => removeSource(addedSource)}
        >
          <SourceRemoveIconWrapper>
            <Icon name='close' />
          </SourceRemoveIconWrapper>
        </Button>
      </StreamSourcesListItem>
    );
  });

  const isEmpty = list.length === 0;

  return (
    <StreamSourcesListWrapper>
      {isEmpty ? noSourcesPill : list}
    </StreamSourcesListWrapper>
  );
};

const StreamSourcesListWrapper = styled.div`
  display: flex;
  flex-wrap: wrap;
`;

// The 500px border radius is a CSS hack to get the pill shape
const StreamSourcesListItem = styled.div.attrs({
  className: 'cu-text-subsection-2 cu-text-truncate',
})<ISourcePillProps>`
  && {
    border-radius: 500px;
    display: flex;
    justify-content: center;
    align-items: center;
    ${({ isDisabled }) => isDisabled && `opacity: 0.5;`}

    ${({ pillType, theme }) => {
      switch (pillType) {
        case StreamSourcePillType.EMPTY_SOURCE:
          return `
            background-color: ${theme.colors.gray0};
            color: ${theme.colors.gray7};
          `;
        case StreamSourcePillType.EXCLUDED_SOURCE:
          return `
            background-color: ${theme.colors.aliases.bodySecondary};
            color: ${theme.colors.white};
          `;
        case StreamSourcePillType.ADDED_SOURCE:
        default:
          return `
            background-color: ${theme.colors.aliases.primary};
            color: ${theme.colors.white};
          `;
      }
    }}

    ${({ theme }) => {
      return `
        padding: 0px ${theme.spacings[2]};
        margin: ${theme.spacings[2]} ${theme.spacings[2]} ${theme.spacings[2]} 0px;
        height: ${theme.spacings[4]};
      `;
    }}
  }
`;

const SourceRemoveIconWrapper = styled.span`
  display: flex;

  ${({ theme }) => css`
    margin-left: 6px;
    padding-left: 6px;
    align-items: center;
    border-left: 1px solid ${theme.colors.white};
    height: ${theme.spacings[4]};
  `}

  svg {
    width: 13px !important;
    height: 13px !important;
  }
`;

const noSourcesPill = (
  <StreamSourcesListItem pillType={StreamSourcePillType.EMPTY_SOURCE}>
    No sources added yet
  </StreamSourcesListItem>
);
